import type { INotesDesignInfo, IPodListing, IPodListingNotes } from '../constants/upload/data-structures/pod-design/pod-listing.interface';
import type { EnumPlatform, EnumWebsiteOption } from '../constants/upload/app-constants/platforms/print-on-demand.constants';


export type TypeFlyingDesignTransferStatus = 'queued' | 'received' | 'cancelled';
export type TypeFlyingDesignTransferDeviceStatus = 'pending' | 'active' | 'revoked';
export type TypeFlyingDesignTransferImageMime = 'image/png' | 'image/jpeg';

export interface IFlyingDesignTransferDeviceSummary {
	deviceId: string;
	label: string;
	status: TypeFlyingDesignTransferDeviceStatus;
	createdAt: string;
	expiresAt: string;
	revokedAt: string | null;
}

export interface IFlyingDesignTransferPairingRequest {
	label: string;
}

export interface IFlyingDesignTransferPairingGrantResponse {
	deviceId: string;
	label: string;
	grant: string;
	createdAt: string;
	expiresAt: string;
}

export interface IFlyingDesignTransferExchangeRequest {
	grant: string;
}

export interface IFlyingDesignTransferSessionResponse {
	device: IFlyingDesignTransferDeviceSummary;
	sessionToken: string;
	expiresAt: string;
}

export interface IFlyingDesignTransferCreateRequest {
	deviceId: string;
	requestId: string;
	flyIds: string[];
}

export interface IFlyingDesignTransferAckRequest {
	status: 'received';
}

/** Expiry is determined from expiresAt, independently of the persisted status. */
export interface IFlyingDesignTransferSummary extends IFlyingDesignTransferCreateRequest {
	transferId: string;
	status: TypeFlyingDesignTransferStatus;
	createdAt: string;
	expiresAt: string;
	receivedAt: string | null;
	cancelledAt: string | null;
}

export interface IFlyingDesignTransferImage {
	flyId: string;
	fileName: string;
	mimeType: TypeFlyingDesignTransferImageMime;
	byteLength: number;
	etag: string;
}

/** License attachment storage references are not part of a desktop transfer. */
export interface IFlyingDesignTransferNotes extends Omit<IPodListingNotes, 'license' | 'designInfos'> {
	license: { text: string };
	designInfos: Omit<INotesDesignInfo, 'createdAt' | 'uploadedAt'> & {
		createdAt: string;
		uploadedAt: string;
	};
}

export interface IFlyingDesignTransferListing extends Omit<IPodListing, 'notes'> {
	notes: IFlyingDesignTransferNotes | null;
}

/** Upload history is informational; marketplace account emails and arbitrary data are excluded. */
export interface IFlyingDesignTransferUpload {
	platform: EnumPlatform;
	websiteOption: EnumWebsiteOption | null;
	uploadedAt: string;
}

export interface IFlyingDesignTransferDesignSnapshot {
	flyId: string;
	image: IFlyingDesignTransferImage;
	listing: IFlyingDesignTransferListing | null;
	uploads: IFlyingDesignTransferUpload[];
}

/** Assets are requested using transferId and flyId; the manifest contains no asset URLs. */
export interface IFlyingDesignTransferManifest {
	version: 1;
	transferId: string;
	deviceId: string;
	createdAt: string;
	expiresAt: string;
	designs: IFlyingDesignTransferDesignSnapshot[];
}
